import { Link } from "react-router-dom";
import { 
  ShoppingCart, FileText, TrendingUp, TrendingDown, ArrowRight, 
  Leaf, TreePine, Wind, Sun, Factory, Recycle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const marketplaceOptions = [
  {
    title: "Register Carbon Project",
    description: "Register your carbon reduction or removal project to generate verified carbon credits.",
    icon: FileText,
    color: "bg-forest-50 text-forest-700",
    action: "Start Registration"
  },
  {
    title: "Buy Carbon Credits",
    description: "Purchase verified carbon credits from diverse projects to offset your emissions.",
    icon: ShoppingCart,
    color: "bg-earth-50 text-earth-700",
    action: "Browse Credits"
  },
  {
    title: "Sell Carbon Credits",
    description: "List your verified carbon credits on our marketplace and connect with buyers.",
    icon: TrendingUp,
    color: "bg-teal-50 text-teal-600",
    action: "List Credits"
  }
];

const reductionMethods = [
  { name: "Renewable Energy", icon: Sun, description: "Solar, wind, and hydro power replacing fossil fuels" },
  { name: "Energy Efficiency", icon: Factory, description: "Reducing energy consumption in industry and buildings" },
  { name: "Clean Transportation", icon: Wind, description: "Electric vehicles and efficient transport" },
  { name: "Waste Management", icon: Recycle, description: "Methane capture and waste-to-energy projects" },
];

const removalMethods = [
  { name: "Reforestation", icon: TreePine, description: "Planting trees to absorb CO₂" },
  { name: "Afforestation", icon: Leaf, description: "Creating new forests on previously non-forested land" },
  { name: "Blue Carbon", icon: Wind, description: "Mangrove and seagrass ecosystem restoration" },
  { name: "Direct Air Capture", icon: Factory, description: "Technology-based CO₂ removal from atmosphere" },
];

export default function MarketplacePage() {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="marketplace-page">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-forest-50 to-earth-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white text-forest-700 px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-sm">
            <ShoppingCart className="h-4 w-4" />
            Carbon Credit Marketplace
          </div>
          <h1 className="font-montserrat text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
            India's Carbon Credit
            <span className="gradient-text block">Marketplace</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Register projects, buy or sell carbon credits, and participate in the climate economy.
          </p>
        </div>
      </section>

      {/* Main Options */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {marketplaceOptions.map((option) => (
              <Card key={option.title} className="h-full hover:shadow-lg transition-shadow" data-testid={`marketplace-${option.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6 lg:p-8">
                  <div className={`w-14 h-14 ${option.color} rounded-2xl flex items-center justify-center mb-6`}>
                    <option.icon className="h-7 w-7" />
                  </div>
                  <h3 className="font-montserrat text-xl font-bold text-slate-900 mb-3">{option.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{option.description}</p>
                  <Button className="w-full bg-forest-950 hover:bg-forest-900">
                    {option.action}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Carbon Market Education */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-montserrat text-3xl font-bold text-slate-900 mb-4">Carbon Market Education</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Understand the methods that generate carbon credits
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Reduction Methods */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingDown className="h-5 w-5 text-green-700" />
                </div>
                <h3 className="font-montserrat text-xl font-semibold text-slate-900">Carbon Reduction Methods</h3>
              </div>
              <div className="space-y-4">
                {reductionMethods.map((method) => (
                  <div key={method.name} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="p-2 bg-white rounded-lg">
                      <method.icon className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{method.name}</h4>
                      <p className="text-sm text-slate-600">{method.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Removal Methods */}
            <div className="bg-white rounded-2xl p-6 lg:p-8 border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <Leaf className="h-5 w-5 text-teal-700" />
                </div>
                <h3 className="font-montserrat text-xl font-semibold text-slate-900">Carbon Removal Methods</h3>
              </div>
              <div className="space-y-4">
                {removalMethods.map((method) => (
                  <div key={method.name} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="p-2 bg-white rounded-lg">
                      <method.icon className="h-4 w-4 text-teal-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{method.name}</h4>
                      <p className="text-sm text-slate-600">{method.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-forest-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-montserrat text-3xl font-bold mb-4">Ready to Participate?</h2>
          <p className="text-forest-200 mb-8 max-w-2xl mx-auto">
            Whether you're looking to offset your emissions, register a project, or trade credits, 
            our team is here to guide you through the process.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-forest-950 hover:bg-forest-50">
                Talk to Our Team
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Contact Our Team
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
